/* Normalizzazioni condivise dalle due schermate.
 *
 * Stanno insieme perché la stessa riga va descritta allo stesso modo nella
 * tabella di un gruppo, nella scheda del telefono, nel selettore ricercabile e
 * nel confronto fra preferiti: quattro copie della stessa frase avrebbero
 * finito per divergere proprio sul periodo di misura, che è la parte che non
 * può divergere.
 */

import { periodo as fmtPeriodo, timeframe } from '../../lib/formato';
import type { Backtest, BacktestGroup, BacktestRiga } from '../../api/tipi';

/* ==========================================================================
 * GRUPPI
 * ======================================================================== */

/** Il gruppo dichiara i timeframe in un array: in griglia ce n'è quasi sempre
 *  uno solo, ma quando sono più di uno dirne uno solo sarebbe una bugia. */
export function timeframeDiGruppo(g: Pick<BacktestGroup, 'timeframeArray'>): string {
  const valori = (g.timeframeArray ?? [])
    .map((t) => t.candleSize)
    .filter((v): v is number => typeof v === 'number');
  if (valori.length === 0) return '—';
  if (valori.length === 1) return timeframe(valori[0]);
  return `${valori.length} timeframe`;
}

export function periodoDiGruppo(g: Pick<BacktestGroup, 'startDate' | 'endDate'>): string {
  return fmtPeriodo(g.startDate, g.endDate);
}

/** La coppia. Se la valuta non è fra i campi restituiti si mostra il solo
 *  asset: inventare "USDT" sarebbe un dato finto su cui poi si decide. */
export function coppiaDiGruppo(g: { asset?: string; currency?: string }): string {
  if (!g.asset) return '—';
  return g.currency ? `${g.asset}/${g.currency}` : g.asset;
}

/** La riga sotto il nome: strategia, coppia, timeframe e — sempre — periodo. */
export function descrizioneGruppo(
  g: Pick<BacktestGroup, 'strategyName' | 'timeframeArray' | 'startDate' | 'endDate'> & {
    asset?: string;
    currency?: string;
  },
): string {
  return [g.strategyName, coppiaDiGruppo(g), timeframeDiGruppo(g), periodoDiGruppo(g)].join(' · ');
}

/** Testo su cui cerca il selettore: strategia, asset, valuta, anni e id. */
export function testoRicercaGruppo(g: GruppoInElenco): string {
  return [String(g.id), g.strategyName ?? '', g.asset ?? '', g.currency ?? '', periodoDiGruppo(g)]
    .join(' ')
    .toLowerCase();
}

/** Quel che serve a disegnare una voce dell'elenco gruppi. È volutamente un
 *  sottoinsieme di `BacktestGroup`: le faccette ne restituiscono meno campi
 *  della lista, e il selettore deve funzionare con entrambe. */
export type GruppoInElenco = Pick<
  BacktestGroup,
  'id' | 'strategyName' | 'asset' | 'startDate' | 'endDate' | 'timeframeArray' | 'backtestNumber'
> & { currency?: string; isRunning?: boolean };

/* ==========================================================================
 * PARAMETRI DI UNA CONFIGURAZIONE
 * ======================================================================== */

/**
 * I parametri come sono stati mandati al motore, non riformattati: un
 * `warmupPeriod` di 1000 scritto "1.000" all'italiana sembrerebbe un altro
 * valore, e questi numeri servono a ritrovare la configurazione, non a essere
 * letti come importi.
 */
export function parametriLeggibili(params: Record<string, unknown> | null): string {
  if (!params) return '—';
  const voci = Object.entries(params)
    .filter(([, v]) => v !== null && v !== undefined && typeof v !== 'object')
    .map(([k, v]) => `${k} ${String(v)}`);
  return voci.length ? voci.join(' · ') : '—';
}

/* ==========================================================================
 * PREFERITI — la proiezione leggera
 * ======================================================================== */

/**
 * `GET /backtester?isFavorite=true` risponde con le ENTITY COMPLETE: dentro ci
 * sono `trades`, `candlesClosePrices` ed `equityLinePoints`, cioè megabyte per
 * riga. Il peso sulla rete non si può evitare finché l'endpoint è quello, ma
 * il peso IN MEMORIA sì: si proietta subito su questa forma e l'array grezzo
 * resta irraggiungibile, quindi il garbage collector se lo riprende.
 *
 * Il tipo elenca i campi uno per uno di proposito. Un `Omit<Backtest, …>`
 * avrebbe continuato a compilare il giorno in cui il backend aggiunge una
 * serie nuova, e quella serie sarebbe rimasta in memoria senza che nessuno se
 * ne accorgesse.
 */
export interface PreferitoLeggero {
  id: number;
  strategyName: string;
  asset: string;
  currency: string;
  candleSize: number;
  startDate: string;
  endDate: string;
  strategyParams: Record<string, unknown> | null;
  strategyChange: number | null;
  marketChange: number | null;
  drawdownMaxValue: number | null;
  tradesNumber: number;
  cagr: number | null;
  calmar: number | null;
  sharpe: number | null;
  walkForwardScore: number | null;
  walkForwardMetodo: 'ciclo' | 'meta' | null;
  bearYearsAvg: number | null;
  bearYearsCount: number | null;
  intorno: number | null;
  intornoParametro: string | null;
  intornoAssi: number | null;
  backtestGroupId: number | null;
  description: string | null;
}

export function proiettaPreferito(b: Backtest): PreferitoLeggero {
  return {
    id: b.id,
    strategyName: b.strategyName,
    asset: b.asset,
    currency: b.currency,
    candleSize: b.candleSize,
    startDate: b.startDate,
    endDate: b.endDate,
    strategyParams: b.strategyParams,
    strategyChange: b.strategyChange,
    marketChange: b.marketChange,
    drawdownMaxValue: b.drawdownMaxValue,
    tradesNumber: b.tradesNumber,
    cagr: b.cagr,
    calmar: b.calmar,
    sharpe: b.sharpe,
    walkForwardScore: b.walkForwardScore,
    walkForwardMetodo: b.walkForwardMetodo,
    bearYearsAvg: b.bearYearsAvg,
    bearYearsCount: b.bearYearsCount,
    intorno: b.intorno,
    intornoParametro: b.intornoParametro,
    intornoAssi: b.intornoAssi,
    backtestGroupId: b.backtestGroupId,
    description: b.description,
  };
}

/** Il periodo di una riga, sempre accanto ai suoi numeri (regola 2). */
export function periodoDiRiga(r: Pick<BacktestRiga, 'startDate' | 'endDate'>): string {
  return fmtPeriodo(r.startDate, r.endDate);
}
